import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Info } from '@phosphor-icons/react';
import { MODULES } from '../moduleRegistry';
import ModuleTypeNav from '../components/ModuleTypeNav';
import MakeItLiveButton from '../components/MakeItLiveButton';
import { useAllowedScreens } from '../utils/screenAccess';
import { useSupportingTools } from '../SupportingToolsContext';

// Every Supporting Tools screen, reachable directly by route — independent of the
// Connect -> Client Workspace flow, which requires a resolved connection-registry
// entry (a live backend) before it will render anything. Useful for browsing/QA'ing
// every screen's layout without needing a real client connection wired up.
const PREVIEW_CONNECTION_ID = 'PREVIEW';

function ScreensPreviewPage() {
  const { moduleKey } = useParams();
  const navigate = useNavigate();
  const { basePath } = useSupportingTools();

  const { canOpen } = useAllowedScreens();

  const selectedModule = moduleKey ? decodeURIComponent(moduleKey) : null;
  // This key comes straight from the URL, so the right is checked here rather
  // than trusted from the nav that produced the link.
  const selected = MODULES.find((m) => m.key === selectedModule && canOpen(m.key));

  const selectModule = (key) => {
    navigate(`${basePath}/screens/${encodeURIComponent(key)}`);
  };

  return (
    <div data-testid="screens-preview-page">
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '16px', padding: '10px 14px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', fontSize: '12px', color: '#92400E' }}>
        <Info size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
        <span>
          Preview mode — screens render with a placeholder connection ID ({PREVIEW_CONNECTION_ID}) and no
          client is resolved, so data-fetching calls will fail until a real backend is reachable. This is
          for browsing every screen's layout, not for making real changes.
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', gap: '20px', alignItems: 'start' }}>
        <ModuleTypeNav
          heading={`All Screens (${MODULES.length})`}
          selectedKey={selectedModule}
          onSelect={selectModule}
          testIdPrefix="preview-module"
          maxHeight="760px"
        />
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              {selected ? selected.label : 'Screen'}
            </div>
            <MakeItLiveButton screen={selected} />
          </div>
          {!selected && (
            <div
              style={{ padding: '40px 24px', textAlign: 'center', background: 'white', border: '1px dashed #E5E7EB', borderRadius: '8px', color: '#9CA3AF', fontSize: '14px' }}
              data-testid="preview-empty"
            >
              {selectedModule && !selected
                ? 'You do not have access to this screen.'
                : 'Pick a type on the left, then a screen inside it, to preview it.'}
            </div>
          )}
          {selected && (
            // A screen wider than the pane scrolls sideways here, with the bar under the screen.
            <div style={{ overflowX: 'auto' }} data-testid="screen-scroll">
              <selected.Component connectionId={PREVIEW_CONNECTION_ID} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScreensPreviewPage;
